import { z } from 'zod'
import type { Observatory } from './music.ts'
import { Level, Pc, ScaleId } from './music.ts'
import { Licence, SongId, StoryMusicMap } from './song.ts'

/** `catalogue-src/<songId>/source.json`, hand-written by the curator (spec §14). */
export const Source = z.object({
  id: SongId,
  source: z.string().regex(/^[a-z0-9]+$/),
  sourceId: z.string().min(1),
  title: z.string().min(1),
  artist: z.string().min(1),
  genre: z.string(),
  trackUrl: z.string().url(),
  artistUrl: z.string().url().optional(),
  licence: Licence,
  licenceUrl: z.string().url(),
  verifiedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'ISO date'),
  archiveUrl: z.string().url().optional(),
  notes: z.string().optional(),
})
export type Source = z.infer<typeof Source>

/**
 * A scaffolded source file before the curator has filled it in: the same shape with the
 * required fields allowed to be empty. `ingest new` writes this; `derive` requires `Source`.
 */
export const SourceDraft = z.object({
  id: SongId,
  source: z.string(),
  sourceId: z.string(),
  title: z.string().optional(),
  artist: z.string().optional(),
  genre: z.string().optional(),
  trackUrl: z.string().optional(),
  artistUrl: z.string().optional(),
  licence: z.string().optional(),
  licenceUrl: z.string().optional(),
  verifiedAt: z.string().optional(),
  archiveUrl: z.string().optional(),
  notes: z.string().optional(),
})
export type SourceDraft = z.infer<typeof SourceDraft>

/** `detector.json`: envelope plus the raw Music.ai output, never modified. */
export const Detector = z.object({
  workflow: z.string().min(1),
  jobId: z.string().min(1),
  analysedAt: z.string(),
  inputSha256: z.string().regex(/^[0-9a-f]{64}$/),
  outputs: z.record(z.string(), z.unknown()),
})
export type Detector = z.infer<typeof Detector>

export const CurationStatus = z.enum(['candidate', 'accepted', 'rejected', 'withdrawn'])
export type CurationStatus = z.infer<typeof CurationStatus>

export const ChordCuration = z.object({
  quiz: z.boolean(),
  /**
   * The chord starts a new passage: the quiz span running through it is split before it (spec
   * §7, §14). Only with `quiz`; where the chord before it in the song is not a quiz chord the
   * flag does nothing and the song does not carry it.
   */
  passage: z.boolean().optional(),
  /**
   * The chord is not a chord of the song: the previous chord runs on to its end (spec §14). Set
   * where the detector chopped one sounding chord into slivers read off the melody. Never on the
   * first chord; `quiz` on a merged chord is ignored.
   */
  merge: z.boolean().optional(),
  /**
   * The chord corrected by ear, as an absolute chord symbol in the detector's notation (`C`,
   * `G/B`, `Am7`); the label follows from the engine (spec §14). Not together with `merge`.
   */
  override: z.string().optional(),
})
export type ChordCuration = z.infer<typeof ChordCuration>

/** What the tonic-and-mode heuristic proposed, kept for the curator's reference. */
export const Proposal = z.object({
  tonic: Pc,
  scale: ScaleId,
  confident: z.boolean(),
  consistency: z.number().min(0).max(1),
  margin: z.number(),
  flags: z.array(z.string()),
})
export type Proposal = z.infer<typeof Proposal>

/** A player's error report in the song's log (spec §14), pulled from `error_reports` by `ingest reports`. */
export const ReportLogEntry = z.object({
  id: z.string(),
  /** The annotation version the player heard. */
  annotationVersion: z.number().int().min(1).optional(),
  chordIndex: z.number().int().min(0).nullable(),
  note: z.string(),
  createdAt: z.string(),
  status: z.enum(['open', 'resolved', 'rejected']),
  resolution: z.string().optional(),
})
export type ReportLogEntry = z.infer<typeof ReportLogEntry>

/** `curation.json`: everything the curator decides (spec §2, §14). */
export const Curation = z.object({
  tonic: Pc,
  scale: ScaleId,
  status: CurationStatus,
  level: Level.optional(),
  demo: z.boolean(),
  /** Per chord of the detector's merged list, by index. */
  chords: z.array(ChordCuration),
  /** Median bass MIDI note of the quiz chords (spec §15), recomputed by derive. */
  proposal: Proposal.optional(),
  /** The date the curator confirmed the song by ear; only accepted songs with it are published (§14). */
  reviewedAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'ISO date')
    .optional(),
  notes: z.string().optional(),
  reports: z.array(ReportLogEntry).default([]),
})
export type Curation = z.infer<typeof Curation>

/**
 * `catalogue-src/progression.json`, written by the curator (spec §8, §14): per observatory the
 * number of charted songs that meets each night, indexed by night − 1; 0 where the observatory has
 * nothing at that night. A missing observatory or file means `P.neededDefault` for every night.
 * `story` names the music of each story scene (visual §6), carried into the snapshot.
 */
export const Progression = z.object({
  needed: z.record(z.string(), z.array(z.number().int().min(0)).length(7)),
  story: StoryMusicMap.optional(),
})
export type Progression = z.infer<typeof Progression>

/** The threshold of a night in an observatory from the curator's file, `fallback` where unset. */
export function neededOf(
  progression: Progression | null | undefined,
  observatory: Observatory,
  level: Level,
  fallback: number,
): number {
  return progression?.needed[observatory]?.[level - 1] ?? fallback
}

/**
 * Whether publish takes a song (spec §14): accepted, and ticked as reviewed by the curator;
 * `accepted` alone is the review pass's word.
 */
export const isPublishable = (c: {
  status?: string | null | undefined
  reviewedAt?: string | null | undefined
}): boolean => c.status === 'accepted' && !!c.reviewedAt
